/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, HasProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cutWireSettings: getSettingsCollection(state).cutWireSettings,
});

type ProductBadge = {
    DisplayName: string;
    Description: string;
    Image: string;
};

type Props = WidgetProps & HasProduct & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductBadgesStyles {
    container?: InjectableCss;
    badgeWrapper?: InjectableCss;
    badgeImage?: LazyImageProps;
    badgeText?: TypographyProps;
}

export const badgesStyles: ProductBadgesStyles = {
    container: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px 22px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid ${color("common.border")};
            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr;
            }
        `,
    },
    badgeWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 20px 1fr;
            gap: 8px;
            align-items: center;
        `,
    },
    badgeImage: {
        css: css`
            padding: 1px;
            img {
                object-fit: contain;
                aspect-ratio: 1;
            }
        `,
    },
    badgeText: {
        size: fontConfig.singleLinexSmall.size,
        lineHeight: fontConfig.singleLinexSmall.lineHeight,
    },
};

const styles = badgesStyles;

const ProductBadges: React.FC<Props> = ({ product }) => {
    const isLoading = (product as XProductModel).isLoading;
    if (isLoading) {
        return (
            <StyledWrapper {...styles.container}>
                <Skeleton height="28px" width="100%" />
            </StyledWrapper>
        );
    }
    if (product?.isVariantParent || !product?.properties?.badges) {
        return null;
    }

    const parsedBadges = JSON.parse(product.properties["badges"]);
    const badges: ProductBadge[] = parsedBadges.map((badge: ProductBadge) => {
        if (badge.DisplayName === "PROP_65_CANCER") {
            badge.Description = product.properties.prop65Text || (siteMessage("Custom_Prop65DefaultText") as string);
        }

        return badge;
    });

    return (
        <StyledWrapper {...styles.container}>
            {badges.map(
                badge =>
                    badge.Image && (
                        <StyledWrapper {...styles.badgeWrapper} key={badge.DisplayName}>
                            <LazyImage src={badge.Image} altText={badge.DisplayName} {...styles.badgeImage} />
                            <Typography {...styles.badgeText}>{badge.Description}</Typography>
                        </StyledWrapper>
                    ),
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductBadges)),
    definition: {
        displayName: "Product Badges",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
